/**
 * Gerätefähigkeiten aus Inventory / SecurityInfo / DEP-Profil.
 * User Enrollment und fehlende Supervision schränken MDM-Aktionen ein.
 * DEP-Soll (is_supervised) greift als Fallback, solange Inventory noch fehlt.
 */

export function isUserEnrollment(device) {
  const security = device?.SecurityInfo || {};
  if (security.IsUserEnrollment === true) return true;
  if (security.ManagementStatus?.IsUserEnrollment === true) return true;
  return false;
}

export function isSupervised(device) {
  const inv = device?.QueryResponses?.IsSupervised;
  if (inv === true) return true;
  if (inv === false) return false;
  // Inventory noch leer → ADE-Profil-Soll
  return device?.enrollmentContext?.depIsSupervised === true;
}

export function isEnrolledViaDep(device) {
  const security = device?.SecurityInfo || {};
  if (security.EnrolledViaDEP === true) return true;
  return device?.enrollmentContext?.hasDepAssignment === true;
}

/**
 * @returns {{
 *   supervised: boolean,
 *   userEnrollment: boolean,
 *   dep: boolean,
 *   enrollmentLabel: string,
 *   canClearPasscode: boolean,
 *   canErase: boolean,
 *   canLostMode: boolean,
 *   canActivationLockBypass: boolean,
 *   canInstallProfile: boolean,
 *   canLock: boolean,
 *   canRestart: boolean,
 *   canShutdown: boolean,
 *   canRemoteDesktop: boolean,
 *   canBluetooth: boolean,
 *   canTimeZone: boolean,
 * }}
 */
export function getDeviceCapabilities(device) {
  const supervised = isSupervised(device);
  const userEnrollment = isUserEnrollment(device);
  const dep = isEnrolledViaDep(device);
  const mdm = device?.mdmProfileInstalled === true;
  const invSupervised = device?.QueryResponses?.IsSupervised;
  const supervisedFromDepOnly = invSupervised == null && device?.enrollmentContext?.depIsSupervised === true;

  let enrollmentLabel = 'Device Enrollment';
  if (userEnrollment) enrollmentLabel = 'User Enrollment';
  else if (dep) enrollmentLabel = 'ADE / DEP';
  if (supervised) {
    enrollmentLabel += supervisedFromDepOnly ? ' · supervised (DEP)' : ' · supervised';
  } else if (!userEnrollment) {
    enrollmentLabel += ' · unsupervised';
  }

  return {
    supervised,
    userEnrollment,
    dep,
    enrollmentLabel,
    // ClearPasscode: nicht für User Enrollment
    canClearPasscode: mdm && !userEnrollment,
    // Erase: UE erlaubt eingeschränktes Wipe; volle RTS/Lost-Mode-Flows brauchen Device Enrollment
    canErase: mdm,
    canLostMode: mdm && supervised && !userEnrollment,
    canActivationLockBypass: mdm && (supervised || dep) && !userEnrollment,
    canInstallProfile: mdm,
    canLock: mdm,
    canRestart: mdm,
    // ShutDownDevice: supervised (iOS/iPadOS/tvOS); Mac ADE ebenfalls supervised
    canShutdown: mdm && supervised && !userEnrollment,
    canRemoteDesktop: mdm && !userEnrollment,
    canBluetooth: mdm && supervised && !userEnrollment,
    canTimeZone: mdm && !userEnrollment,
  };
}

/**
 * Filtert Action-Buttons. Unterstützte Keys an Button-Objekten:
 * - capability: 'canLostMode' | ...
 * - requiresSupervised: true
 * - hideForUserEnrollment: true
 */
export function filterActionButtons(device, actionButtons = []) {
  const caps = getDeviceCapabilities(device);
  return (actionButtons || []).filter((button) => {
    if (!button) return false;
    if (button.requiresSupervised && !caps.supervised) return false;
    if (button.hideForUserEnrollment && caps.userEnrollment) return false;
    if (button.capability && caps[button.capability] === false) return false;
    return true;
  });
}
